// src/utils/legacyProgress.js
//
// The one-time move from the old gamification store to the plain progress
// record (stores/progressStore.js), October 2026.
//
// Until then the app kept XP, a ten-level ladder, achievements and streaks
// (docs/REVIEW_2026-10.md, Decision 1: removed). Two things in that store were
// never a game and are kept: which modules you have finished, and the
// insights you saved. Everything else is dropped, and the old keys go.
//
// This module is pure (no React, no Firebase) so the move can be tested and
// can run before the progress store reads its own key.
//
// Australian English throughout.

// Where the plain record lives in this browser (zustand persist).
export const PROGRESS_KEY = 'friendships-vibe-progress';
// The progress store's persist version; the record written here must match it
// or zustand refuses to load it.
export const PROGRESS_VERSION = 1;

// Every key the removed gamification wrote in localStorage.
//   friendships-vibe-gamification  the old zustand store (XP, level, streaks,
//                                  achievements, completed modules, insights)
//   vibeGamificationData(_<uid>)   the older context's per-account copy
//   tipOfTheDay_lastRead(Id)       "read today's tip" ticks that gated +5 XP
//   module_<n>_exercises           ticks from the old module viewer's exercise
//                                  list ("Complete Exercise & Claim 50 XP");
//                                  that viewer is gone and nothing reads them
export const LEGACY_GAME_KEYS = [
  'friendships-vibe-gamification',
  'vibeGamificationData',
  'tipOfTheDay_lastRead',
  'tipOfTheDay_lastReadId',
];
export const LEGACY_GAME_PREFIXES = ['vibeGamificationData_'];
const LEGACY_EXERCISE_KEY = /^module_\d+_exercises$/;

// Fields of the old database document (users/{uid}/gamification/progress) and
// the older top-level gamification/{uid} that were game mechanics. None of
// them is read any more; a document that still has any of them is rewritten
// without them, once. (`completedTools` fed the "Tools Mastered" ring, which
// nothing ever advanced.)
export const LEGACY_GAME_FIELDS = [
  'totalXP',
  'currentLevel',
  'unlockedAchievements',
  'pendingAchievements',
  'currentStreak',
  'longestStreak',
  'helpedOthers',
  'lastActivity',
  'completedTools',
];

const isLegacyKey = (key) =>
  typeof key === 'string' &&
  (LEGACY_GAME_KEYS.includes(key) ||
    LEGACY_GAME_PREFIXES.some((p) => key.startsWith(p)) ||
    LEGACY_EXERCISE_KEY.test(key));

const parse = (raw) => {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Module ids, as numbers, once each, in order. Anything else is dropped. */
export const normaliseModuleIds = (...lists) => {
  const ids = new Set();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      const id = Number(raw);
      if (Number.isInteger(id) && id > 0) ids.add(id);
    }
  }
  return [...ids].sort((a, b) => a - b);
};

/**
 * Saved insights from several copies, once each (by id), newest first. An
 * insight without an id is kept (it can't be a duplicate we can recognise).
 */
export const mergeInsights = (...lists) => {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const insight of list) {
      if (!insight || typeof insight !== 'object') continue;
      if (insight.id != null) {
        const id = String(insight.id);
        if (seen.has(id)) continue;
        seen.add(id);
      }
      out.push(insight);
    }
  }
  const at = (i) => Date.parse(i?.timestamp || '') || 0;
  return out.sort((a, b) => at(b) - at(a));
};

/** Earliest known completion date per module. */
export const mergeCompletedAt = (...maps) => {
  const out = {};
  for (const map of maps) {
    if (!map || typeof map !== 'object') continue;
    for (const [id, when] of Object.entries(map)) {
      if (typeof when !== 'string' || !Date.parse(when)) continue;
      if (!out[id] || Date.parse(when) < Date.parse(out[id])) out[id] = when;
    }
  }
  return out;
};

/** The plain record, and nothing else, from any object that might hold one. */
export const toRecord = (source = {}) => ({
  completedModules: normaliseModuleIds(source?.completedModules),
  moduleCompletedAt: mergeCompletedAt(source?.moduleCompletedAt),
  savedInsights: mergeInsights(source?.savedInsights),
});

/** Does a stored object still carry any game field? */
export const hasLegacyGameFields = (data) =>
  !!data && typeof data === 'object' && LEGACY_GAME_FIELDS.some((f) => f in data);

const keysOf = (store) => {
  const keys = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null) keys.push(key);
    }
  } catch {
    // Storage disabled: nothing to list.
  }
  return keys;
};

const defaultStore = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : globalThis.localStorage;
  } catch {
    return undefined;
  }
};

/**
 * Move what is worth keeping out of the old gamification keys into the
 * progress record, then remove the old keys. Safe to run on every load: once
 * the old keys are gone it finds nothing and does nothing. Never throws.
 *
 * The old keys are removed only after the record has been written, so a
 * failed write (storage full, private mode) loses nothing: the next load
 * tries again.
 *
 * Returns { moved: boolean, removed: [keys] }.
 */
export function migrateLegacyProgress(store = defaultStore()) {
  const result = { moved: false, removed: [] };
  if (!store) return result;

  const legacyKeys = keysOf(store).filter(isLegacyKey);
  if (legacyKeys.length === 0) return result;

  // What the old stores held that is worth keeping.
  const sources = legacyKeys
    .map((key) => {
      const data = parse(store.getItem(key));
      // zustand persist wraps the state: { state: {...}, version }.
      return data && typeof data === 'object' && data.state && typeof data.state === 'object'
        ? data.state
        : data;
    })
    .filter((data) => data && typeof data === 'object');

  const current = parse(store.getItem(PROGRESS_KEY));
  const currentState = current?.state && typeof current.state === 'object' ? current.state : {};

  const record = {
    completedModules: normaliseModuleIds(
      currentState.completedModules,
      ...sources.map((s) => s.completedModules),
    ),
    moduleCompletedAt: mergeCompletedAt(currentState.moduleCompletedAt),
    savedInsights: mergeInsights(currentState.savedInsights, ...sources.map((s) => s.savedInsights)),
  };

  try {
    store.setItem(PROGRESS_KEY, JSON.stringify({ state: record, version: PROGRESS_VERSION }));
    result.moved = sources.length > 0;
  } catch {
    // Couldn't write the record: keep the old keys so nothing is lost.
    return result;
  }

  for (const key of legacyKeys) {
    try {
      store.removeItem(key);
      result.removed.push(key);
    } catch {
      // Keep going: one stuck key must not stop the rest.
    }
  }
  return result;
}

export default { migrateLegacyProgress, toRecord, hasLegacyGameFields };
